import { useEffect, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

export default function VideoWorkbench() {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [tasks, setTasks] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = 'Video Workbench — Pixano';
  }, []);

  useEffect(() => {
    const loadTasks = async () => {
      const tasksSnap = await getDocs(collection(db, 'tasks'));
      const tasksData: Record<string, any> = {};
      tasksSnap.docs.forEach((d) => {
        tasksData[d.id] = { id: d.id, ...d.data() };
      });
      setTasks(tasksData);
    };
    loadTasks();
  }, []);

  useEffect(() => {
    if (Object.keys(tasks).length === 0) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'submissions'),
        where('status', 'in', ['approved', 'needs_relabeling']),
        where('taskType', '==', 'Video')
      ),
      (snapshot) => {
        const items: any[] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const available = items
          .filter((sub) => {
            if (!sub.trimmedVideoUrl) return false;
            const task = tasks[sub.taskId];
            if (!task) return false;
            return task.detection?.enabled && task.detection?.schemaRef?.id;
          })
          .map((sub) => ({
            ...sub,
            task: tasks[sub.taskId],
          }));

        available.sort((a, b) => {
          const aTime =
            typeof a.createdAt === 'number'
              ? a.createdAt
              : a.createdAt &&
                  typeof a.createdAt === 'object' &&
                  'toMillis' in a.createdAt
                ? (a.createdAt as any).toMillis()
                : 0;
          const bTime =
            typeof b.createdAt === 'number'
              ? b.createdAt
              : b.createdAt &&
                  typeof b.createdAt === 'object' &&
                  'toMillis' in b.createdAt
                ? (b.createdAt as any).toMillis()
                : 0;
          return aTime - bTime;
        });

        setSubmissions(available);
        setLoading(false);

        setSelected((prev: any) => {
          if (!prev) return available[0] || null;
          if (available.find((s) => s.id === prev.id)) return prev;
          return available[0] || null;
        });
      }
    );

    return () => unsubscribe();
  }, [tasks]);

  const handleSendToReview = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      await updateDoc(doc(db, 'submissions', selected.id), {
        status: 'labeled',
      });
      const updated = submissions.filter((p) => p.id !== selected.id);
      setSubmissions(updated);
      setSelected(updated.length > 0 ? updated[0] : null);
    } catch (error: any) {
      console.error('Error sending to review:', error);
      alert(`Error: ${error?.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const validationPrompt =
    selected?.validationDetails?.prompt ||
    selected?.validationPrompt ||
    selected?.task?.validationPrompt ||
    '-';

  return (
    <div>
      <div className="border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold mb-4">Video Workbench</h1>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 text-gray-600">No videos in queue</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2 border p-4 rounded">
            {selected ? (
              <div>
                <div className="mb-2">
                  <div className="text-xl">
                    <span className="text-gray-700 font-bold">Task:</span>{' '}
                    <span className="text-gray-900">{selected.task?.title || '-'}</span>
                  </div>
                </div>
                <div className="mb-2 flex flex-wrap items-center gap-6 text-base">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Category:</span>
                    <span className="text-gray-900">{selected.task?.category || selected.category || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Subcategory:</span>
                    <span className="text-gray-900">{selected.task?.subcategory || selected.subcategory || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Item Type:</span>
                    <span className="text-gray-900">{selected.itemType || '-'}</span>
                  </div>
                </div>

                {selected.videoMetadata && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Video Source Metadata</div>
                    <div className="space-y-1">
                      <div className="text-sm text-gray-600">
                        Device:{' '}
                        {selected.videoMetadata.isMetaGlasses
                          ? 'Ray-Ban Meta Smart Glasses'
                          : selected.videoMetadata.deviceModel || 'Unknown'}
                      </div>
                      <div className="text-sm text-gray-600">
                        Copyright:{' '}
                        {selected.videoMetadata.isMetaGlasses
                          ? 'Meta AI'
                          : selected.videoMetadata.copyright || 'Unknown'}
                      </div>
                      {selected.videoMetadata.width > 0 && selected.videoMetadata.height > 0 && (
                        <div className="text-sm text-gray-600">
                          Dimensions: {selected.videoMetadata.width}×{selected.videoMetadata.height}
                        </div>
                      )}
                      {selected.videoMetadata.duration > 0 && (
                        <div className="text-sm text-gray-600">
                          Duration: {selected.videoMetadata.duration.toFixed(1)}s
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-4 mb-2">
                  <h3 className="font-semibold mb-2">Validation Prompt</h3>
                  <div className="text-base text-gray-900 whitespace-pre-wrap min-h-[48px]">
                    {validationPrompt}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-sm text-gray-600 mb-1">Trimmed Video</div>
                  <div className="mt-1 w-full rounded border bg-black" style={{ maxHeight: 500 }}>
                    <video
                      src={selected.trimmedVideoUrl}
                      controls
                      className="w-full"
                      style={{ maxHeight: 500 }}
                    />
                  </div>
                </div>

                <button
                  className={`px-4 py-2 rounded ${
                    sending
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                  onClick={handleSendToReview}
                  disabled={sending}
                >
                  {sending ? 'Sending...' : 'Send to Review'}
                </button>
              </div>
            ) : (
              <div className="text-gray-600">Select a submission from the queue</div>
            )}
          </div>

          <div className="lg:col-span-1 border p-4 rounded flex flex-col overflow-hidden" style={{ maxHeight: '80vh' }}>
            <h3 className="font-semibold mb-3">Queue ({submissions.length})</h3>
            <ul className="overflow-y-auto flex-1 pr-2 space-y-2">
              {submissions.map((p) => (
                <li
                  key={p.id}
                  className={`p-2 rounded cursor-pointer border ${
                    selected?.id === p.id ? 'bg-blue-100 border-blue-300' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setSelected(p)}
                >
                  <div className="font-medium text-sm truncate">{p.task?.title || 'Unknown'}</div>
                  <div className="text-xs text-gray-600">{p.task?.category || '-'}</div>
                  <div className="text-xs text-gray-500 truncate">{p.trimmedVideoUrl ? 'Trimmed' : ''}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
